  import { Component, signal, computed, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Site, SiteStatus, TlColor } from './screens.types';

@Component({
  selector: 'app-reseau-screen',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './reseau-screen.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./reseau-screen.component.scss'],
})

export class ReseauScreenComponent {
  @Output() siteSelected = new EventEmitter<Site>();

  readonly sites: Site[] = [
    {
      id: 'nantes-centre', nom: 'NANTES CENTRE', statut: 'critique',
      tensionLabel: 'TENSION CRITIQUE', descInfo: 'Dégradation continue',
      activite: 'très élevée', activiteLevel: 'critique', presenceEstimee: 38,
      alertes: [
        { text: 'Alerte envoyée', type: 'default' },
        { text: 'Mode dégradé non activé', type: 'warn' },
      ],
      showActionBtns: false,
      timeline: ['vert', 'vert', 'orange', 'orange', 'rouge', 'rouge'],
      summary: '"Tension continue depuis 45 min"',
    },
    {
      id: 'saint-herblain', nom: 'SAINT-HERBLAIN', statut: 'moderee',
      tensionLabel: 'TENSION MODÉRÉE', descInfo: 'Flux en augmentation',
      activite: 'importante', activiteLevel: 'moderee', presenceEstimee: 14,
      alertes: [{ text: 'Surveillance active', type: 'default' }],
      showActionBtns: true,
      timeline: ['vert', 'vert', 'vert', 'orange', 'orange', 'orange'],
      summary: '"Tension depuis 30 min"',
    },
    {
      id: 'reze', nom: 'REZÉ', statut: 'fluide',
      tensionLabel: 'FLUIDE', descInfo: null,
      activite: 'normale', activiteLevel: 'fluide', presenceEstimee: 5,
      alertes: [{ text: 'Disponible pour soutien', type: 'ok' }],
      showActionBtns: false,
      timeline: ['vert', 'vert', 'vert', 'vert', 'vert', 'vert'],
      summary: '"Situation stable"',
    },
    {
      id: 'beaujoire', nom: 'NANTES BEAUJOIRE', statut: 'fluide',
      tensionLabel: 'FLUIDE', descInfo: null,
      activite: 'faible', activiteLevel: 'fluide', presenceEstimee: 3,
      alertes: [{ text: 'Disponible pour soutien', type: 'ok' }],
      showActionBtns: false,
      timeline: ['vert', 'vert', 'vert', 'vert', 'vert', 'vert'],
      summary: '"Situation stable"',
    },
    {
      id: 'carquefou', nom: 'CARQUEFOU', statut: 'moderee',
      tensionLabel: 'TENSION MODÉRÉE', descInfo: 'Flux en augmentation',
      activite: 'importante', activiteLevel: 'moderee', presenceEstimee: 16,
      alertes: [{ text: 'Surveillance active', type: 'default' }],
      showActionBtns: true,
      timeline: ['vert', 'vert', 'vert', 'vert', 'orange', 'orange'],
      summary: '"Tension depuis 15 min"',
    },
    {
      id: 'orvault', nom: 'ORVAULT', statut: 'fluide',
      tensionLabel: 'FLUIDE', descInfo: null,
      activite: 'normale', activiteLevel: 'fluide', presenceEstimee: 7,
      alertes: [{ text: 'Disponible pour soutien', type: 'ok' }],
      showActionBtns: false,
      timeline: ['vert', 'vert', 'orange', 'vert', 'vert', 'vert'],
      summary: '"Situation stable"',
    },
    {
      id: 'vertou', nom: 'VERTOU', statut: 'fluide',
      tensionLabel: 'FLUIDE', descInfo: null,
      activite: 'faible', activiteLevel: 'fluide', presenceEstimee: 3,
      alertes: [{ text: 'Disponible pour soutien', type: 'ok' }],
      showActionBtns: false,
      timeline: ['vert', 'vert', 'vert', 'vert', 'vert', 'vert'],
      summary: '"Situation stable"',
    },
    {
      id: 'la-chapelle', nom: 'LA CHAPELLE', statut: 'ferme',
      tensionLabel: 'SITE FERMÉ', descInfo: 'Aucune activité',
      activite: '', activiteLevel: 'ferme', presenceEstimee: 0,
      alertes: [], showActionBtns: false,
      timeline: [],
      summary: '',
    },
  ];

  readonly activeFilter = signal<SiteStatus | null>(null);

  readonly filteredSites = computed(() => {
    const f = this.activeFilter();
    return f ? this.sites.filter(s => s.statut === f) : this.sites;
  });

  setFilter(status: SiteStatus | null) {
    if (status === null) {
      this.activeFilter.set(null);
    } else {
      this.activeFilter.update(current => current === status ? null : status);
    }
  }

  critiques() { return this.sites.filter(s => s.statut === 'critique').length; }
  moderees()  { return this.sites.filter(s => s.statut === 'moderee').length; }
  fluides()   { return this.sites.filter(s => s.statut === 'fluide').length; }

  getTimelineSlots(): string[] {
    const now = new Date();
    const slots: string[] = [];
    for (let i = 5; i >= 0; i--) {
      const t = new Date(now.getTime() - i * 15 * 60000);
      const h = String(t.getHours()).padStart(2, '0');
      const m = String(Math.floor(t.getMinutes() / 15) * 15).padStart(2, '0');
      slots.push(`${h}:${m}`);
    }
    return slots;
  }

  tlColor(c: TlColor): string {
    const map: Record<TlColor, string> = { vert: '#10B981', orange: '#F59E0B', rouge: '#EF4444' };
    return map[c];
  }
}
